import { api, apiWithLoading, apiBackground } from './api'
import { Contact } from './contacts'

export interface Call {
  id: string
  contactId?: string
  campaignId?: string
  campaignContactId?: string
  userId?: string
  status: 'QUEUED' | 'DIALING' | 'ANSWERED' | 'IN_PROGRESS' | 'NO_ANSWER' | 'BUSY' | 'FAILED'
  outcome?: 'NO_ANSWER' | 'INTERESTED' | 'NOT_INTERESTED' | 'CALLBACK'
  direction?: 'INBOUND' | 'OUTBOUND'
  startedAt?: string
  endedAt?: string
  duration?: number
  twilioSid?: string
  twilioFromNumber?: string | null
  twilioToNumber?: string | null
  scheduledCallbackTime?: string  // ✅ Pour les callbacks programmés
  contact?: Contact
  notes?: string
  createdAt?: string
  updatedAt?: string
  user?: {
    id: string
    email: string
    name?: string
  }
  recording?: {
    id: string
    url: string | null
    duration: number | null
  } | null
}

/**
 * #468 — origine d'un rappel servi par `GET /calls/callbacks/upcoming` :
 * `CALL` = appel qualifié « Rappel » ; `ASSISTANT` = fiche de campagne écrite
 * par l'assistant IA (trace d'audit) ; `CAMPAIGN_CONTACT` = fiche « Rappel
 * prévu » sans appel ni trace.
 */
export type CallbackOrigin = 'CALL' | 'ASSISTANT' | 'CAMPAIGN_CONTACT'

/**
 * Ligne de « Rappels à venir ». Une ligne-fiche n'est PAS un appel : `id`
 * préfixé `fiche:`, `callId: null`, ni statut ni enregistrement — aucun écran
 * ne doit s'en servir comme d'un id d'appel.
 */
export type UpcomingCallbackRow = Omit<Call, 'status'> & {
  status?: Call['status']
  callId?: string | null
  callbackOrigin?: CallbackOrigin
  callbackRequestedBy?: { id: string; name: string | null } | null
  campaign?: { id: string; name: string } | null
}

export interface CreateCallDto {
  contactId?: string
  campaignId?: string
  twilioSid?: string  // CallSid Twilio pour les appels manuels
  phone?: string // Numéro appelé pour les appels manuels
  status?: string // Status initial (QUEUED, etc.)
  notes?: string
}

export interface UpdateCallDto {
  contactId?: string  // For associating call with contact after creation
  userId?: string     // For tracking which agent handled the call
  status?: Call['status']
  outcome?: Call['outcome']
  duration?: number
  notes?: string
}

export interface CallOutcomeDto {
  outcome: Call['outcome']
  notes?: string
}

export interface DialerNextContactResponse {
  callId: string
  contact: {
    id: string
    name: string
    phone: string
    email?: string
  }
  campaign: {
    id: string
    name: string
  }
  queuePosition: number
  estimatedWaitTime: number
  priority: number
}

export class CallsService {
  private static readonly BASE_PATH = '/calls'
  private static readonly DIALER_PATH = '/dialer'

  // Get all calls (supports paginated response from backend)
  static async getAll(params?: {
    contactId?: string
    campaignId?: string
    status?: Call['status']
    outcome?: Call['outcome']
    userId?: string // ✅ For filtering agent-specific stats
    page?: number
    limit?: number
  }): Promise<Call[]> {
    // Use apiBackground to avoid loading modal
    const response = await apiBackground.get(
      CallsService.BASE_PATH,
      { params }
    )
    // Handle both paginated {data, total, ...} and legacy array responses
    const result = response.data
    return Array.isArray(result) ? result : (result.data ?? [])
  }

  // Get call by ID
  static async getById(id: string): Promise<Call> {
    // Use apiBackground to avoid loading modal
    const response = await apiBackground.get(
      `${CallsService.BASE_PATH}/${id}`
    )
    return response.data
  }

  // Find recent call by multiple criteria (for incoming call matching)
  static async findRecentCall(params: {
    contactId?: string
    phone?: string
    userId?: string
    direction?: 'INBOUND' | 'OUTBOUND'
    seconds?: number
  }): Promise<Call> {
    // Use apiBackground to avoid loading modal and error toast
    const response = await apiBackground.get(
      `${CallsService.BASE_PATH}/find-recent`,
      { params }
    )
    return response.data
  }

  // Create new call
  static async create(call: CreateCallDto): Promise<Call> {
    // Use apiBackground to avoid loading modal during auto-dial
    const response = await apiBackground.post(
      CallsService.BASE_PATH,
      call
    )
    return response.data
  }

  // Update call by ID
  static async update(id: string, call: UpdateCallDto): Promise<Call> {
    // Use apiBackground to avoid loading modal during auto-dial
    const response = await apiBackground.patch(
      `${CallsService.BASE_PATH}/${id}`,
      call
    )
    return response.data
  }

  // Update call by Twilio SID (for WebRTC manual calls)
  static async updateByTwilioSid(twilioSid: string, call: UpdateCallDto): Promise<Call> {
    const response = await api.patch(
      `${CallsService.BASE_PATH}/by-twilio-sid/${twilioSid}`,
      call,
      { headers: { 'X-Background-Request': 'true' } }
    )
    return response.data
  }

  // Delete call
  static async delete(id: string): Promise<void> {
    await apiWithLoading.delete(
      `${CallsService.BASE_PATH}/${id}`,
      'Deleting call...'
    )
  }

  // DIALER METHODS

  // Get next contact to call
  static async getNextContact(campaignId?: string): Promise<DialerNextContactResponse | null> {
    try {
      const params = new URLSearchParams();
      if (campaignId) {
        params.append('campaignId', campaignId);
      }
      
      // Use apiBackground to avoid loading modal during auto-dial
      const response = await apiBackground.get(
        `${CallsService.DIALER_PATH}/next${params.toString() ? `?${params.toString()}` : ''}`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null // No contacts available
      }
      throw error
    }
  }

  // Set call outcome (background request - no loading modal)
  static async setCallOutcome(callId: string, data: { outcome: string; notes?: string; nextCallAt?: string }) {
    return api.post(`/dialer/calls/${callId}/outcome`, data, {
      headers: {
        'X-Background-Request': 'true'
      }
    })
  }

  // Get contact call history
  static async getContactCallHistory(contactId: string) {
    // ⚡ Use apiBackground to avoid loader during call flow
    const response = await apiBackground.get(`/calls/contact/${contactId}/history`)
    return response.data
  }

  // Get upcoming callbacks (next 24h) - background request
  // #468 : union des appels qualifiés « Rappel » et des fiches de campagne
  // « Rappel prévu » sans appel (posées par l'assistant) — voir UpcomingCallbackRow.
  static async getUpcomingCallbacks(): Promise<UpcomingCallbackRow[]> {
    const response = await api.get(`${CallsService.BASE_PATH}/callbacks/upcoming`, {
      headers: {
        'X-Background-Request': 'true'
      }
    })
    return response.data
  }

  // Start dialing (initiate call)
  static async startDialing(contactId: string, campaignId?: string): Promise<Call> {
    const response = await apiWithLoading.post(
      `${CallsService.DIALER_PATH}/start`,
      { contactId, campaignId },
      'Starting call...'
    )
    return response.data
  }

  // Get call statistics
  static async getCallStats(params?: {
    campaignId?: string
    startDate?: string
    endDate?: string
  }): Promise<{
    totalCalls: number
    successfulCalls: number
    failedCalls: number
    averageDuration: number
    outcomeBreakdown: Record<string, number>
  }> {
    const response = await apiWithLoading.get(
      `${CallsService.BASE_PATH}/stats`,
      'Loading call statistics...',
      { params }
    )
    return response.data
  }

  // ✅ FIX: Get today's stats (for autodial counter) - uses dedicated endpoint
  static async getTodayStats(): Promise<{
    totalCalls: number
    answeredCalls: number
    totalDuration: number
    avgDuration: number
    answerRate: number
  }> {
    const response = await apiBackground.get(`${CallsService.BASE_PATH}/today-stats`)
    return response.data
  }

  // ✅ NEW: Get aggregated analytics stats (no limit, calculated in DB)
  static async getAnalyticsStats(params?: {
    campaignId?: string
    userId?: string
  }): Promise<{
    totalCalls: number
    todayCalls: number
    answeredCalls: number
    totalDurationMinutes: number
    avgDurationSeconds: number
    answerRate: number
    byStatus: {
      answered: number
      noAnswer: number
      busy: number
      failed: number
      dialing: number
      inProgress: number
      queued: number
    }
    byOutcome: {
      interested: number
      notInterested: number
      callback: number
      noAnswer: number
    }
    last7Days: { date: string; count: number; answered?: number }[]
  }> {
    const response = await apiBackground.get(`${CallsService.BASE_PATH}/analytics-stats`, { params })
    return response.data
  }
}

export default CallsService
